import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  InformationCircleIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const Clients = () => {
  // list + loading/errors
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // creation form
  const [form, setForm] = useState({ name: '', phone: '', address: '', caseTypeLabel: '' });

  // case type suggestions for create/edit (unique values from other clients)
  const [caseTypeOptions, setCaseTypeOptions] = useState([]);

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    _id: '',
    name: '',
    phone: '',
    address: '',
    caseTypeLabel: '',
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // ===================== Data fetchers =====================
  const fetchClients = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_URL}/clients`);
      setClients(res.data || []);
    } catch (err) {
      console.error('Failed to load clients', err);
      setError('Failed to load clients.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseTypeOptions = async () => {
    try {
      const res = await axios.get(`${API_URL}/clients/case-type-options`);
      const opts = (res.data?.options || []).map((o) => ({
        label: o.label || o.key || 'Other',
        key: o.key || 'other',
        count: o.count || 0,
      }));
      setCaseTypeOptions(opts);
    } catch (err) {
      // ok if none yet (first client)
      console.warn('Could not load case type options.', err?.message);
      setCaseTypeOptions([]);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchCaseTypeOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===================== Create =====================
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await axios.post(`${API_URL}/clients`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        caseTypeLabel: form.caseTypeLabel.trim() || undefined,
      });
      setForm({ name: '', phone: '', address: '', caseTypeLabel: '' });
      fetchClients();
      fetchCaseTypeOptions();
    } catch (err) {
      console.error('Failed to save client', err);
      const msg = err?.response?.data?.error || 'Failed to save client.';
      setError(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await axios.delete(`${API_URL}/clients/${id}`);
      fetchClients();
      fetchCaseTypeOptions();
    } catch (err) {
      console.error('Failed to delete client', err);
      alert('Failed to delete client.');
    }
  };

  // ===================== Edit =====================
  const openEdit = (c) => {
    setEditError('');
    setEditForm({
      _id: c._id,
      name: c.name || '',
      phone: c.phone || '',
      address: c.address || '',
      caseTypeLabel: c.caseTypeLabel || c.caseType || '',
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditSaving(false);
    setEditError('');
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm._id) return;

    try {
      setEditSaving(true);
      setEditError('');

      await axios.put(`${API_URL}/clients/${editForm._id}`, {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(), // cuidado: phone é unique; backend validará
        address: editForm.address.trim(),
        caseTypeLabel: (editForm.caseTypeLabel || '').trim() || undefined,
      });

      closeEdit();
      fetchClients();
      fetchCaseTypeOptions();
    } catch (err) {
      console.error('Failed to update client', err);
      const msg = err?.response?.data?.error || 'Failed to update client.';
      setEditError(msg);
      setEditSaving(false);
    }
  };

  // ===================== UI pieces =====================
  const InfoBanner = () => (
    <div className="mb-8 rounded-xl border border-blue-200 bg-blue-50 px-4 py-5">
      <div className="flex items-start gap-3">
        <InformationCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Client Management</h2>
          <p className="mt-1 text-gray-700 text-sm">
            Create, edit and manage clients. Set a free-text <strong>Case Type</strong> for each client.
            From the second client on, you&apos;ll get unique suggestions previously used by others.
          </p>
        </div>
      </div>
    </div>
  );

  // ===================== Render =====================
  return (
    <div className="px-6 py-4">
      <InfoBanner />

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Clients</h2>
        <button
          onClick={() => {
            fetchClients();
            fetchCaseTypeOptions();
          }}
          className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded"
          title="Refresh"
        >
          <ArrowPathIcon className="h-5 w-5" />
          Refresh
        </button>
      </div>

      {/* Create form */}
      <form onSubmit={handleSubmit} className="bg-white shadow rounded p-4 mb-8 space-y-4">
        {error && (
          <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              className="mt-1 w-full border rounded px-3 py-2"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Phone</label>
            <input
              type="text"
              className="mt-1 w-full border rounded px-3 py-2"
              placeholder="+1..."
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Address</label>
            <input
              type="text"
              className="mt-1 w-full border rounded px-3 py-2"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Case Type <span className="text-gray-400 font-normal">(free text with suggestions)</span>
            </label>
            <input
              type="text"
              list="caseTypeOptions"
              className="mt-1 w-full border rounded px-3 py-2"
              placeholder="e.g., Car Accident, Slip & Fall, Dog Bite"
              value={form.caseTypeLabel}
              onChange={(e) => setForm({ ...form, caseTypeLabel: e.target.value })}
            />
            <datalist id="caseTypeOptions">
              {caseTypeOptions.map((opt) => (
                <option key={opt.key} value={opt.label}>
                  {opt.label} ({opt.count})
                </option>
              ))}
            </datalist>
            <p className="text-xs text-gray-500 mt-1">
              Suggestions are unique values gathered from other clients.
            </p>
          </div>
        </div>

        <div className="pt-2">
          <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            Save
          </button>
        </div>
      </form>

      {/* List */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left bg-white rounded shadow">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Phone</th>
              <th className="px-4 py-2">Address</th>
              <th className="px-4 py-2">Case Type</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c._id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-2">{c.name || '-'}</td>
                <td className="px-4 py-2">{c.phone || '-'}</td>
                <td className="px-4 py-2">{c.address || '-'}</td>
                <td className="px-4 py-2">{c.caseTypeLabel || c.caseType || '-'}</td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-4">
                    <button
                      onClick={() => openEdit(c)}
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(c._id)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && clients.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center text-gray-500 py-6">
                  No clients found.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan="5" className="text-center text-gray-400 py-6">
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={editSaving ? undefined : closeEdit}
          />
          {/* dialog */}
          <div className="relative z-50 w-full max-w-xl bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Client</h3>
              <button
                onClick={closeEdit}
                className="p-1 rounded hover:bg-gray-100"
                disabled={editSaving}
                title="Close"
              >
                <XMarkIcon className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            {editError && (
              <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 mb-3">
                {editError}
              </div>
            )}

            <form onSubmit={submitEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  className="mt-1 w-full border rounded px-3 py-2"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="text"
                  className="mt-1 w-full border rounded px-3 py-2"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="+1..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Note: phone must be unique across clients.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <input
                  type="text"
                  className="mt-1 w-full border rounded px-3 py-2"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Case Type <span className="text-gray-400 font-normal">(free text with suggestions)</span>
                </label>
                <input
                  type="text"
                  list="caseTypeOptionsEdit"
                  className="mt-1 w-full border rounded px-3 py-2"
                  value={editForm.caseTypeLabel}
                  onChange={(e) => setEditForm({ ...editForm, caseTypeLabel: e.target.value })}
                  placeholder="e.g., Car Accident"
                />
                <datalist id="caseTypeOptionsEdit">
                  {caseTypeOptions.map((opt) => (
                    <option key={opt.key} value={opt.label}>
                      {opt.label} ({opt.count})
                    </option>
                  ))}
                </datalist>
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 rounded text-white ${editSaving ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                  disabled={editSaving}
                >
                  {editSaving ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
